import {
  Component,
  ViewChild,
  OnInit,
  input,
  signal,
  effect,
  TemplateRef,
  computed,
  AfterViewInit,
  output,
  OnDestroy,
  ViewEncapsulation,
  ContentChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import {
  AbbActionsMenuComponent,
  MenuItem,
} from '@components/abb-actions-menu/abb-actions-menu.component';
import { ICONS } from '@constants/icons';
import { COLORS } from '@constants/colors';
import { TPipe } from '@pipes/t.pipe';
import { AbbTableDataSource, DataProvider } from './data-source';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  startWith,
  Subscription,
} from 'rxjs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AbbSwitchComponent } from '@components/abb-switch/abb-switch.component';
import { AbbTimeInputComponent } from '@components/abb-form/abb-time-picker';
import { combineDateTime } from '@utils/datetime';
import { MatSortModule, Sort } from '@angular/material/sort';
import { OverlayService } from '@services/ui/overlay.service';
import { SelectionBarComponent } from '@components/abb-selection-bar/abb-selection-bar.component';

export type FilterOption = {
  value: string | number;
  label: string;
  skipTranslation?: boolean;
  subOptions?: Array<{
    value: string | number;
    label: string;
    skipTranslation?: boolean;
  }>;
};

export type DataTableFilter<TFilters> = {
  id: keyof TFilters;
  filterType?:
    | 'select'
    | 'text'
    | 'date'
    | 'datetime'
    | 'datetimeRange'
    | 'autocomplete'
    | 'switch';
  filterOptions?: Array<FilterOption>;
  label?: string;
  className?: string;
};

export interface IColumn<T> {
  id?: keyof T;
  name: string;
  hidden?: boolean;
  tableHeaderCellClass?: string;
  tableCellClass?: string;
  templateRef?: TemplateRef<unknown>;
  key?: keyof T;
  translate?: boolean;
  sortable?: boolean; // TODO: se utilizzato, aggiungerlo alla ui della tabella e utilizzarlo per manipolare dataSource
}

@Component({
  selector: 'abb-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    OverflowTooltipDirective,
    AbbButtonComponent,
    AbbIconComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    AbbActionsMenuComponent,
    TPipe,
    MatDatepickerModule,
    AbbTimeInputComponent,
    MatAutocompleteModule,
    MatCheckboxModule,
    AbbSwitchComponent,
    MatSortModule,
  ],
  template: `
    <div
      [ngClass]="{
        'bg-abb-background mat-elevation-z8': applyDefaultStyle()
      }"
      class=" w-full h-full flex flex-col"
    >
      @if (filters().length > 0 || dataExport()) {
      <div
        class="flex flex-row flex-wrap gap-4"
        [ngClass]="{ 'py-8 px-10': applyDefaultStyle() }"
      >
        @for (filter of filters(); track filter.id) {
        <div class="flex-1">
          @if (filterControls[filter.id]; as ctrl) { @if(filter.filterType ===
          'switch') {
          <abb-switch
            [switchPosition]="'right'"
            class="block h-full content-center"
            [ngClass]="filter.className"
            [label]="filter.label | t"
            [formControl]="ctrl"
            [id]="filter.id.toString()"
          ></abb-switch>
          } @else if (filter.filterType === 'date';) {
          <mat-form-field
            class="mat-input-element"
            [ngClass]="filter.className"
            subscriptSizing="dynamic"
          >
            <mat-label class="mat-form-field-label">{{
              filter.label | t
            }}</mat-label>
            <input
              matInput
              [id]="filter.id.toString()"
              [matDatepicker]="simpleDatePicker"
              [formControl]="ctrl"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [for]="simpleDatePicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #simpleDatePicker [panelClass]="'panel'">
              <mat-datepicker-actions>
                <div class="flex flex-row w-full gap-5 p-5 justify-end">
                  <abb-button [variant]="'clean'" matDateRangePickerCancel>{{
                    'cancel' | t
                  }}</abb-button>
                  <abb-button matDateRangePickerApply>{{
                    'Apply' | t
                  }}</abb-button>
                </div>
              </mat-datepicker-actions>
            </mat-datepicker>
          </mat-form-field>
          } @else {
          <mat-form-field
            subscriptSizing="dynamic"
            class="mat-input-element"
            [ngClass]="filter.className"
            [id]="filter.id"
            color="primary"
          >
            <mat-label class="mat-form-field-label">{{
              filter.label | t
            }}</mat-label>
            @if (filter.filterType === 'select' && filterControls[filter.id];) {
            <mat-select
              [formControl]="ctrl"
              [id]="filter.id.toString() + '-' + filter.filterType"
              [panelClass]="{
                'select-panel': hasSubOptions(filter.filterOptions),
                panel: true
              }"
            >
              <mat-option id="empty-option" [value]="null">-</mat-option>
              @for (option of filter.filterOptions; track option) {
              @if(option.subOptions){
              <mat-optgroup
                [label]="
                  option.skipTranslation ? option.label : (option['label'] | t)
                "
                class="cursor-default"
              >
                @for (subOption of option.subOptions; track subOption) {
                <mat-option
                  [id]="subOption.label"
                  [value]="option['value'] + '_' + subOption['value']"
                  >{{
                    subOption.skipTranslation
                      ? subOption.label
                      : (subOption['label'] | t)
                  }}</mat-option
                >
                }
              </mat-optgroup>
              } @else {
              <mat-option
                [id]="option['value'].toString()"
                [value]="option['value']"
                >{{
                  option.skipTranslation ? option.label : (option['label'] | t)
                }}</mat-option
              >
              } }
            </mat-select>
            } @else if (filter.filterType === 'autocomplete') {
            <input
              type="text"
              matInput
              [id]="filter.id.toString()"
              [formControl]="ctrl"
              [matAutocomplete]="auto"
            />
            <mat-autocomplete
              #auto="matAutocomplete"
              [displayWith]="autocompleteDisplayWith"
              class="panel"
            >
              @for (option of (filteredOptions[filter.id] | async); track
              option.value) {
              <mat-option [id]="option.label" [value]="option">{{
                option.skipTranslation ? option.label : (option['label'] | t)
              }}</mat-option>
              }
            </mat-autocomplete>
            } @else {
            <input
              matInput
              #input
              [id]="filter.id.toString()"
              [formControl]="ctrl"
            />
            } </mat-form-field
          >}} @if (filterGroupsDate[filter.id]; as group) { @if
          (filter.filterType === 'datetime';) {
          <mat-form-field
            class="[&_.mat-mdc-form-field-infix]:w-[100px] mat-input-element"
            [ngClass]="filter.className"
            subscriptSizing="dynamic"
            ><mat-label class="mat-form-field-label">{{
              filter.label | t
            }}</mat-label>
            <input
              matInput
              [id]="filter.id.toString()"
              [matDatepicker]="datePicker"
              [formControl]="group['date']"
            />
            <span
              class="w-fit max-w-full inline-flex flex-row justify-end flex-grow"
              matTextSuffix
            >
              <input
                [formControl]="group['textTime']"
                type="time"
                class="bg-inherit focus:outline-none text-abb-font/85"
                step="1"
                [ngClass]="
                  group['date'].value ||
                  group['date'].hasError('matDatepickerParse')
                    ? 'cursor-text'
                    : 'opacity-0 focus:opacity-100 pointer-events-none'
                "
                (click)="
                  group['date'].value ||
                  group['date'].hasError('matDatepickerParse')
                    ? $event.stopPropagation()
                    : null
                "
            /></span>
            <mat-datepicker-toggle
              matIconSuffix
              [for]="datePicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #datePicker [panelClass]="'panel'">
              <mat-datepicker-actions>
                <div class="!flex !flex-col !w-full">
                  <abb-time-input
                    class="!w-full !flex !justify-center"
                    [formControl]="group.time"
                  ></abb-time-input>
                  <div class="flex flex-row w-full gap-5 p-5 justify-end">
                    <abb-button [variant]="'clean'" matDateRangePickerCancel>{{
                      'cancel' | t
                    }}</abb-button>
                    <abb-button matDateRangePickerApply>{{
                      'Apply' | t
                    }}</abb-button>
                  </div>
                </div></mat-datepicker-actions
              >
            </mat-datepicker>
          </mat-form-field>
          } } @if (filterGroupsDateRange[filter.id]; as groupRange) {@if
          (filter.filterType === 'datetimeRange';) {
          <mat-form-field
            [formGroup]="groupRange"
            class="[&_.mat-mdc-form-field-infix]:w-[100px] mat-input-element !min-w-[505px]"
            [ngClass]="filter.className"
            subscriptSizing="dynamic"
          >
            <mat-label class="mat-form-field-label">{{
              filter.label | t
            }}</mat-label>
            <mat-date-range-input
              [rangePicker]="dateRangePicker"
              [separator]="
                groupRange.get('endDate')?.value ||
                (!groupRange.get('startDate')?.value &&
                  !groupRange.get('endDate')?.value)
                  ? '–'
                  : ''
              "
            >
              <input
                (ngModelChange)="
                  dateChange($event, groupRange.controls.startTime)
                "
                matStartDate
                [formControl]="groupRange.controls.startDate"
                [placeholder]="'Start date' | t"
                class="placeholder:text-abb-font/40"
              />
              <input
                (ngModelChange)="
                  dateChange($event, groupRange.controls.endTime)
                "
                matEndDate
                [formControl]="groupRange.controls.endDate"
                [placeholder]="'End date' | t"
                class="placeholder:text-abb-font/40"
              />
            </mat-date-range-input>
            <span
              class="w-fit max-w-full inline-flex flex-row justify-end flex-grow"
              matTextSuffix
            >
              @if(!dateRangePicker.opened) {
              <input
                [formControl]="groupRange.controls.startTextTime"
                type="time"
                class="text-right bg-inherit focus:outline-none text-abb-font/85"
                step="1"
                [ngClass]="
                  groupRange.get('startDate')?.value
                    ? 'cursor-text'
                    : 'opacity-0 focus:opacity-100 pointer-events-none'
                "
                (click)="
                  groupRange.get('startDate')?.value
                    ? $event.stopPropagation()
                    : null
                "
              />
              <span class="mat-date-range-input-separator">{{
                groupRange.get('endDate')?.value ? '–' : ''
              }}</span>
              <input
                [formControl]="groupRange.controls.endTextTime"
                type="time"
                class="text-left bg-inherit focus:outline-none text-abb-font/85"
                step="1"
                [ngClass]="
                  groupRange.get('endDate')?.value
                    ? 'cursor-text'
                    : 'opacity-0 focus:opacity-100 pointer-events-none'
                "
                (click)="
                  groupRange.get('endDate')?.value
                    ? $event.stopPropagation()
                    : null
                "
              />} @else {
              <span
                [ngClass]="{
                  'opacity-0 focus:opacity-100':
                    !groupRange.get('startDate')?.value
                }"
              >
                {{ groupRange.get('startTextTime')?.value }}
              </span>
              <span class="mat-date-range-input-separator">{{
                groupRange.get('endDate')?.value ? '–' : ''
              }}</span>
              <span
                [ngClass]="{
                  'opacity-0 focus:opacity-100':
                    !groupRange.get('endDate')?.value
                }"
              >
                {{ groupRange.get('endTextTime')?.value }}
              </span>
              }
            </span>
            <mat-datepicker-toggle
              matIconSuffix
              [for]="dateRangePicker"
            ></mat-datepicker-toggle>
            <mat-date-range-picker #dateRangePicker [panelClass]="'panel'">
              <mat-datepicker-actions>
                <div class="!flex !flex-col !w-full">
                  <div class="!w-full !flex !flex-row !justify-between">
                    @if(dateRangePicker.opened) {
                    <abb-time-input
                      class="!w-full !flex !justify-center"
                      [formControl]="groupRange.controls.startTextTime"
                    ></abb-time-input>
                    <abb-time-input
                      class="!w-full !flex !justify-center"
                      [formControl]="groupRange.controls.endTextTime"
                    ></abb-time-input
                    >}
                  </div>
                  <div class="flex flex-row w-full gap-5 p-5 justify-end">
                    <abb-button [variant]="'clean'" matDateRangePickerCancel>{{
                      'cancel' | t
                    }}</abb-button>
                    <abb-button matDateRangePickerApply>{{
                      'Apply' | t
                    }}</abb-button>
                  </div>
                </div></mat-datepicker-actions
              >
            </mat-date-range-picker>
          </mat-form-field>
          } }
        </div>
        }
        @if (filterTrigger() === 'manual') {
          <abb-button
            class="w-32"
            [loading]="dataSourceLoading()"
            [disabled]="dataSourceLoading()"
            (onclick)="applyAllFilters()"
          >
            {{ 'Apply' | t }}
          </abb-button>
        }
        @if(dataExport()) {
        <div class="flex-1 flex justify-end">
          <abb-button
            id="export"
            class="w-32"
            (onclick)="onExport.emit(dataSource.getLastFilter())"
            [loading]="exporting()"
            [disabled]="exporting()"
          >
            {{ 'Export' | t }}
          </abb-button>
        </div>
        }
      </div>
      }
      <section
        class="w-full h-full flex flex-col gap-8"
        [ngClass]="{ 'px-10': applyDefaultStyle() }"
        tabindex="0"
      >
        <div class="h-full flex flex-col overflow-auto">
          <table
            mat-table
            matSort
            (matSortChange)="sortData($event)"
            [dataSource]="dataSource"
            class="w-full"
            multiTemplateDataRows
            [trackBy]="trackById"
          >
            @if(selectable()){
            <ng-container matColumnDef="select">
              <th mat-header-cell *matHeaderCellDef class="!p-0 w-0">
                <mat-checkbox
                  color="primary"
                  id="select-all"
                  (change)="$event ? toggleAllRows() : null"
                  [checked]="selection?.hasValue() && isAllSelected()"
                  [indeterminate]="selection?.hasValue() && !isAllSelected()"
                  [aria-label]="checkboxLabel()"
                  class="border-font-checkbox no-hover-checkbox"
                >
                </mat-checkbox>
              </th>
              <td
                mat-cell
                *matCellDef="let row; let idx = dataIndex"
                class="!p-0"
              >
                <mat-checkbox
                  color="primary"
                  [id]="row + '-' + idx"
                  (click)="$event.stopPropagation()"
                  (change)="$event ? selection?.toggle(row) : null"
                  [checked]="selection?.isSelected(row)"
                  [aria-label]="checkboxLabel(row, idx)"
                  class="border-font-checkbox no-hover-checkbox"
                >
                </mat-checkbox>
              </td>
            </ng-container>
            } @if (expandable()) {
            <ng-container matColumnDef="expand" sticky>
              <th mat-header-cell *matHeaderCellDef class="w-0">&nbsp;</th>
              <td mat-cell *matCellDef="let item" class="!p-0">
                <button
                  (click)="onExpandRow(item)"
                  id="expand-row"
                  class="text-inherit h-8 w-8 rounded-full inline-flex items-center justify-center"
                >
                  @if (itemCompareFn()(expandedItem(), item)) {
                  <abb-icon name="mat/arrow_drop_down"></abb-icon>
                  } @else {
                  <abb-icon name="mat/arrow_right"></abb-icon>
                  }
                </button>
              </td>
            </ng-container>
            } @for (columnDef of displayColumns(); track columnDef.name; let idx
            = $index) { @if(columnDef.name==="actions") {
            <ng-container matColumnDef="actions" stickyEnd>
              <th
                [ngClass]="columnDef.tableHeaderCellClass ?? ''"
                mat-header-cell
                *matHeaderCellDef
                aria-label="row actions"
              >
                &nbsp;
              </th>
              <td
                mat-cell
                *matCellDef="let item"
                class="!p-0"
                [ngClass]="columnDef.tableCellClass ?? ''"
              >
                <abb-actions-menu
                  [menuItems]="actions()"
                  [rowData]="item"
                  class="flex justify-end"
                />
              </td>
            </ng-container>
            } @else { @if (columnDef.name; as column) {
            <ng-container [matColumnDef]="column">
              @if(columnDef.name === 'no-header-cell'){
              <th
                mat-header-cell
                class="w-0"
                *matHeaderCellDef
                aria-label="row actions"
              >
                &nbsp;
              </th>
              } @else {
              <th
                mat-header-cell
                *matHeaderCellDef
                [mat-sort-header]="column"
                [disabled]="!columnDef.sortable"
                class="text-nowrap w-auto uppercase truncate"
                [ngClass]="columnDef.tableHeaderCellClass ?? ''"
              >
                <span overflowTooltip class="w-auto !truncate">
                  {{ columnDef.translate ? (column | t) : column }}
                </span>
                @if (columnDef.sortable) { }
              </th>
              }

              <td
                mat-cell
                *matCellDef="let item"
                #cellElement
                class="text-nowrap max-w-28"
                [ngClass]="columnDef.tableCellClass ?? ''"
              >
                @if (columnDef.templateRef) {
                <ng-container
                  *ngTemplateOutlet="
                    columnDef.templateRef;
                    context: { $implicit: item }
                  "
                ></ng-container>
                } @else if (columnDef.key) {
                <span overflowTooltip class="block w-full !truncate">
                  {{ columnDef.translate ? (item[columnDef.key] | t) : item[columnDef.key] }}
                </span>
                }
              </td>
            </ng-container>
            }} }

            <tr class="mat-row" *matNoDataRow>
              <td
                class="mat-cell pt-20 pb-12"
                [attr.colspan]="
                  expandable() || selectable()
                    ? displayColumns().length + 1
                    : displayColumns().length
                "
              >
                @if (dataSourceLoading()) {
                <mat-spinner class="mx-auto" [diameter]="50"></mat-spinner>
                }@else if (dataSource.total() === 0) {
                <span class="block text-center">No data</span>
                }
              </td>
            </tr>

            <tr
              mat-header-row
              *matHeaderRowDef="columnNames(); sticky: true"
            ></tr>
            <tr mat-row *matRowDef="let row; columns: columnNames()"></tr>

            @if (expandable()) {
            <tr
              mat-row
              *matRowDef="let row; columns: ['expandedDetail']"
              [ngClass]="{ hidden: !itemCompareFn()(expandedItem(), row) }"
            ></tr>
            <ng-container matColumnDef="expandedDetail">
              <td
                mat-cell
                *matCellDef="let item"
                [attr.colspan]="columnNames().length"
                class="!bg-abb-black/[0.02]"
              >
                <div
                  [ngClass]="{ hidden: !itemCompareFn()(expandedItem(), item) }"
                >
                  <ng-container
                    *ngTemplateOutlet="
                      expandedTemplateRef();
                      context: { $implicit: item }
                    "
                  ></ng-container>
                </div>
              </td>
            </ng-container>
            }
          </table>
        </div>
        @if (showPagination()) {

        <mat-paginator
          [length]="dataSource.total()"
          [pageSize]="pageSize()"
          [hidePageSize]="true"
          [className]="'text-abb-font'"
        ></mat-paginator>
        }@else if(customPaginatorTemplate()) {
        <ng-container
          *ngTemplateOutlet="customPaginatorTemplate()"
        ></ng-container>
        }
      </section>
    </div>
  `,
  host: {
    class: 'flex flex-row pr-4 pb-4',
  },
  encapsulation: ViewEncapsulation.None,
  styles: `
    .select-panel {
      .mat-mdc-optgroup {
        --mdc-list-list-item-label-text-color: var(--abb-color-font) !important;
      }
      .mat-mdc-optgroup-label {
        opacity: 0.6 !important;
      }
    }

    .mat-column-select {
      overflow: initial;
    }
  `,
})
export class AbbTableComponent<T, TFilters extends Record<string, any>>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  columns = input.required<Array<IColumn<T>>>();
  dataProvider = input.required<DataProvider<T, TFilters>>();
  defaultFilters = input.required<TFilters>();
  filters = input.required<Array<DataTableFilter<TFilters>>>();
  filterTrigger = input<'manual' | 'auto'>('auto');
  onManualFiltersApplied = output<Partial<TFilters>>();
  defaultSortBy = input<{ column: keyof T; order: 'asc' | 'desc' }[]>([]);
  loading = input<boolean>(true);
  expandable = input<boolean>(false);
  selectable = input<boolean>(false);
  initialSelection = input<Array<T>>([]);
  allowMultiSelect = input<boolean>(true);
  dataExport = input<boolean>(false);
  exporting = input<boolean>(false);
  pageSize = input(8);
  showPagination = input(true);
  onExpand = output<T | null>();
  onExport = output<TFilters | undefined>();
  dataSourceLoading = computed(() => this.loading());
  actions = input<Array<MenuItem<T>>>([]);
  expandedItem = signal<T | null>(null);
  expandedTemplateRef = input<TemplateRef<unknown> | null>(null);
  customPaginatorTemplate = input<TemplateRef<unknown> | null>(null);
  itemCompareFn = input<(a: T | null, b: T | null) => boolean>(
    (a, b) => a === b
  );
  applyDefaultStyle = input(true);

  hasSubOptions(filterOptions: Array<FilterOption> | undefined): boolean {
    return !!filterOptions?.some((o) => o?.subOptions);
  }

  dateChange(e: Date, ctrl: FormControl) {
    if (!e) {
      ctrl.reset();
    }
  }

  // TODO: animazione della riga collassabile

  displayColumns = computed(() => this.columns().filter((c) => !c.hidden));
  columnNames = computed(() => {
    let colNames = this.displayColumns().map((c) => c.name);
    if (this.expandable()) {
      colNames = ['expand', ...colNames];
    }
    if (this.selectable()) {
      colNames = ['select', ...colNames];
    }
    return colNames;
  });
  dataSource: AbbTableDataSource<T, TFilters> = new AbbTableDataSource();
  selection?: SelectionModel<T>;
  readonly ICONS: ICONS = ICONS;
  readonly COLORS: COLORS = COLORS;
  private readonly subscriptions: Subscription[] = [];

  constructor(private readonly overlayService: OverlayService) {
    effect(() => {
      this.filters().forEach((f) => {
        if (f.filterType === 'autocomplete') {
          if (this.optionsSubjects[f.id]) {
            this.optionsSubjects[f.id]?.next(f.filterOptions ?? []);
          } else {
            this.optionsSubjects[f.id] = new BehaviorSubject(
              f.filterOptions ?? []
            );
          }
        }
        if (!this.filterControls[f.id]) {
          this.createFilterControls(f);
          this.subscribeToValueChanges(f.id, f.filterType);
        }
      });
    });
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  ngAfterViewInit(): void {
    if (this.paginator) {
      this.dataSource.setPaginator(this.paginator);
      this.paginator.page.subscribe((event) => {
        this.selection?.clear();
      });
    }
  }

  ngOnInit(): void {
    this.dataSource.setDataProvider(this.dataProvider());
    this.dataSource.setFilters(this.defaultFilters());
    this.dataSource.setSortParams(this.defaultSortBy());
    if (this.selectable()) {
      this.selection = new SelectionModel<T>(
        this.allowMultiSelect(),
        this.initialSelection()
      );
      this.subscriptions.push(
        this.selection.changed.subscribe((s) => {
          if (
            s.source.selected?.length > 0 &&
            s.source.selected?.length === s.added?.length
          ) {
            this.openBottomSheet();
          }
        })
      );
    }
  }

  trackById(index: number, item: any): number {
    return item.id;
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      this.dataSource.setSortParams(this.defaultSortBy());
    } else {
      const sortParam = [
        {
          column: this.columns().find((c) => c.name === sort.active)?.id!,
          order: sort.direction,
        },
      ];
      this.dataSource.setSortParams(sortParam);
    }
  }

  filterControls: Partial<Record<keyof TFilters, FormControl>> = {};
  filterGroupsDate: Partial<
    Record<
      keyof TFilters,
      {
        date: FormControl<Date | null>;
        time: FormControl<string | null>;
        textTime: FormControl<string | null>;
      }
    >
  > = {};
  filterGroupsDateRange: Partial<
    Record<
      keyof TFilters,
      FormGroup<{
        startDate: FormControl<Date | null>;
        startTime: FormControl<string | null>;
        startTextTime: FormControl<string | null>;
        endDate: FormControl<Date | null>;
        endTime: FormControl<string | null>;
        endTextTime: FormControl<string | null>;
      }>
    >
  > = {};
  optionsSubjects: Partial<
    Record<keyof TFilters, BehaviorSubject<FilterOption[]>>
  > = {};
  filteredOptions: Partial<Record<keyof TFilters, Observable<FilterOption[]>>> =
    {};
  createFilterControls({ filterType, id }: DataTableFilter<TFilters>): void {
    if (filterType !== 'datetime' && filterType !== 'datetimeRange') {
      this.filterControls[id] = new FormControl(this.defaultFilters()[id]);
      if (filterType === 'autocomplete') {
        this.filteredOptions[id] = combineLatest([
          this.optionsSubjects[id] as BehaviorSubject<FilterOption[]>,
          this.filterControls[id]!.valueChanges.pipe(startWith('')),
        ]).pipe(
          filter(([_, filter]) => typeof filter === 'string'),
          map(([options, filter]) => {
            return options.filter((o) =>
              o.label.toLowerCase().includes((filter ?? '').toLowerCase())
            );
          })
        );
      }
    } else if (filterType === 'datetime') {
      this.filterGroupsDate[id] = {
        date: new FormControl<Date | null>(null),
        time: new FormControl('00:00:00'),
        textTime: new FormControl('00:00:00'),
      };
    } else {
      this.filterGroupsDateRange[id] = new FormGroup({
        startDate: new FormControl<Date | null>(null),
        startTime: new FormControl<string | null>('00:00:00'),
        startTextTime: new FormControl<string | null>(null),
        endDate: new FormControl<Date | null>(null),
        endTime: new FormControl<string | null>('00:00:00'),
        endTextTime: new FormControl<string | null>(null),
      });
    }
  }

  subscribeToValueChanges(
    fColName: keyof TFilters,
    type: DataTableFilter<TFilters>['filterType']
  ): void {
    let sub: Subscription | undefined;
    if (type === 'datetime') {
      const group = this.filterGroupsDate[fColName];
      if (group) {
        sub = combineLatest([
          group.date.valueChanges,
          // TODO: capire se come valore 400ms è ragionevole o se va alzato/abbassato
          merge(group.time.valueChanges, group.textTime.valueChanges).pipe(
            debounceTime(400)
          ),
        ]).subscribe(([date, time]) => {
          if (this.filterTrigger() === 'auto') {
            this.resetPaginator();
            this.dataSource.setFilters({
              ...(this.dataSource.getLastFilter() ?? this.defaultFilters()),
              [fColName]: date ? combineDateTime(date, time ?? '') : null,
            });
          }
        });
        sub.add(
          group.textTime.valueChanges.subscribe((v) => {
            if (this.filterTrigger() === 'auto') {
              this.resetPaginator();
            }
            group.time.setValue(v, { emitEvent: false });
          })
        );
        sub.add(
          group.time.valueChanges.subscribe((v) => {
            if (this.filterTrigger() === 'auto') {
              this.resetPaginator();
            }
            group.textTime.setValue(v, { emitEvent: false });
          })
        );
      }
    } else if (type === 'datetimeRange') {
      const group = this.filterGroupsDateRange[fColName];
      if (group) {
        const startDateChanges = group.controls.startDate.valueChanges;
        const startTimeChanges = merge(
          group.controls.startTime.valueChanges,
          group.controls.startTextTime.valueChanges
        ).pipe(debounceTime(400));

        const startSub = merge(startDateChanges, startTimeChanges).subscribe(
          () => {
            if (this.filterTrigger() === 'auto') {
              this.resetPaginator();
              const startDate = group.controls.startDate.value;
              const startTime =
                group.controls.startTime.value ??
                group.controls.startTextTime.value;

              this.dataSource.setFilters({
                ...(this.dataSource.getLastFilter() ?? this.defaultFilters()),
                [fColName]: {
                  ...(this.dataSource.getLastFilter() ?? this.defaultFilters())[
                    fColName
                  ],
                  start: {
                    date: startDate
                      ? combineDateTime(startDate, startTime ?? '')
                      : null,
                    time: !!startTime,
                  },
                },
              });
            }
          }
        );

        startSub.add(
          group.controls.startTextTime.valueChanges.subscribe((v) => {
            this.resetPaginator();
            group.controls.startTime.setValue(v, { emitEvent: false });
          })
        );
        startSub.add(
          group.controls.startTime.valueChanges.subscribe((v) => {
            this.resetPaginator();
            group.controls.startTextTime.setValue(v, { emitEvent: false });
          })
        );

        const endDateChanges = group.controls.endDate.valueChanges;
        const endTimeChanges = merge(
          group.controls.endTime.valueChanges,
          group.controls.endTextTime.valueChanges
        ).pipe(debounceTime(400));
        const endSub = merge(endDateChanges, endTimeChanges).subscribe(() => {
          this.resetPaginator();
          const endDate = group.controls.endDate.value;
          const endTime =
            group.controls.endTime.value ?? group.controls.endTextTime.value;

          this.dataSource.setFilters({
            ...(this.dataSource.getLastFilter() ?? this.defaultFilters()),
            [fColName]: {
              ...(this.dataSource.getLastFilter() ?? this.defaultFilters())[
                fColName
              ],
              end: {
                date: endDate ? combineDateTime(endDate, endTime ?? '') : null,
                time: !!endTime,
              },
            },
          });
        });

        endSub.add(
          group.controls.endTextTime.valueChanges.subscribe((v) => {
            if (this.filterTrigger() === 'auto') {
              this.resetPaginator();
            }
            group.controls.endTime.setValue(v, { emitEvent: false });
          })
        );
        endSub.add(
          group.controls.endTime.valueChanges.subscribe((v) => {
            if (this.filterTrigger() === 'auto') {
              this.resetPaginator();
            }
            group.controls.endTextTime.setValue(v, { emitEvent: false });
          })
        );

        this.subscriptions.push(startSub, endSub);
      }
    } else if (type === 'autocomplete') {
      const ctrl = this.filterControls[fColName];
      if (ctrl) {
        sub = ctrl.valueChanges
          /* .pipe(filter((v) => v?.value)) */
          .subscribe((value: TFilters[keyof TFilters]) => {
            if (this.filterTrigger() === 'auto') {
              this.resetPaginator();
              this.dataSource.setFilters({
                ...(this.dataSource.getLastFilter() ?? this.defaultFilters()),
                [fColName]: value?.value ?? this.defaultFilters()[fColName],
              });
            }
          });
      }
    } else {
      const ctrl = this.filterControls[fColName];
      if (ctrl) {
        sub = ctrl.valueChanges.subscribe((value: TFilters[keyof TFilters]) => {
          if (this.filterTrigger() === 'auto') {
            this.resetPaginator();
            this.dataSource.setFilters({
              ...(this.dataSource.getLastFilter() ?? this.defaultFilters()),
              [fColName]: value,
            });
          }
        });
      }
    }
    if (sub) {
      this.subscriptions.push(sub);
    }
  }

  private resetPaginator(): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  onExpandRow(row: T) {
    if (this.expandedItem() === row) {
      this.onExpand.emit(null);
      this.expandedItem.set(null);
    } else {
      this.onExpand.emit(row);
      this.expandedItem.set(row);
    }
  }

  autocompleteDisplayWith(option?: FilterOption) {
    return option?.label ?? '';
  }

  isAllSelected() {
    const numSelected = this.selection?.selected.length;
    const numRows = this.dataSource.items().length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection?.clear();
      return;
    }
    this.selection?.select(...this.dataSource.items());
  }

  checkboxLabel(row?: T, idx?: number): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection?.isSelected(row) ? 'deselect' : 'select'} row ${
      idx! + 2
    }`;
  }

  openBottomSheet() {
    this.overlayService.openBottomSheet(SelectionBarComponent<T>, {
      selection: this.selection,
      actions: this.actions(),
    });
  }

  applyAllFilters() {
    this.resetPaginator();
    this.onManualFiltersApplied.emit(
      Object.fromEntries(
        this.filters().flatMap((f) => {
          const fColName = f.id;
          const type = f.filterType;
          if (type === 'datetime') {
            const group = this.filterGroupsDate[fColName];
            if (group) {
              return [
                [
                  fColName,
                  group.date.value
                    ? combineDateTime(group.date.value, group.time.value ?? '')
                    : undefined,
                ],
              ];
            }
          } else if (type === 'datetimeRange') {
            const group = this.filterGroupsDateRange[fColName];
            if (group) {
              const startDate = group.controls.startDate.value;
              const startTime =
                group.controls.startTime.value ??
                group.controls.startTextTime.value;
              const endDate = group.controls.endDate.value;
              const endTime =
                group.controls.endTime.value ??
                group.controls.endTextTime.value;

              return [
                [
                  fColName,
                  {
                    start: {
                      date: startDate
                        ? combineDateTime(startDate, startTime ?? '')
                        : null,
                      time: !!startTime,
                    },
                    end: {
                      date: endDate
                        ? combineDateTime(endDate, endTime ?? '')
                        : null,
                      time: !!endTime,
                    },
                  },
                ],
              ];
            }
          } else if (type === 'autocomplete') {
            const ctrl = this.filterControls[fColName];
            return [[fColName, ctrl?.value ?? this.defaultFilters()[fColName]]];
          } else {
            const ctrl = this.filterControls[fColName];
            if (ctrl) {
              return [[fColName, ctrl.value]];
            }
          }
          return [];
        })
      ) as Partial<TFilters>
    );
  }
}
